import { useEffect, useMemo, useState } from 'react';
import { RefreshCw, Search, Trash2, Users } from 'lucide-react';
import {
  aspireApi,
  type AdminUserRow,
  type AspireCrewLeader,
  type PickedUser,
  type UserRole,
  usersApi,
} from '../lib/api';
import { ConfirmModal } from './ConfirmModal';
import { DataTable, type ColumnDef } from './DataTable';
import { SearchableSelect } from './SearchableSelect';

const ROLES: UserRole[] = ['User', 'Manager', 'Admin'];

type StatusFilter = 'all' | 'active' | 'disabled';
type SortKey = 'display_name' | 'email' | 'role' | 'is_active';

/**
 * Admin-only screen: list `user_profiles` rows, change roles, soft-delete,
 * and add new users via the Entra people picker (Microsoft Graph read-only).
 *
 * Per MIGRATION_PLAN.md §3.2:
 *   - Add user: pick from enabled tenant users → POST /api/users
 *   - Edit role: PUT /api/users/:id { role }
 *   - Remove: DELETE /api/users/:id (soft-delete, never touches Entra)
 */
export function UserManagement() {
  const [rows, setRows] = useState<AdminUserRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [removeConfirmId, setRemoveConfirmId] = useState<string | null>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [sortKey, setSortKey] = useState<SortKey>('display_name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [crewLeaders, setCrewLeaders] = useState<AspireCrewLeader[]>([]);

  const refresh = () => {
    setError(null);
    setLoading(true);
    usersApi
      .list()
      .then(setRows)
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(refresh, []);

  // Aspire crew leaders for the per-user mapping picker. Degrades silently to an
  // empty list (the column then just shows "Unassigned" with no options).
  useEffect(() => {
    aspireApi
      .crewLeaders()
      .then(setCrewLeaders)
      .catch(() => setCrewLeaders([]));
  }, []);

  const handleRoleChange = async (id: string, role: UserRole) => {
    try {
      const updated = await usersApi.updateRole(id, role);
      setRows((prev) => prev?.map((r) => (r.id === id ? updated : r)) ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Update failed');
    }
  };

  const handleCrewLeaderChange = async (id: string, contactId: number | null) => {
    try {
      const updated = await usersApi.updateCrewLeader(id, contactId);
      setRows((prev) => prev?.map((r) => (r.id === id ? updated : r)) ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Update failed');
    }
  };

  const handleRemove = (id: string) => {
    setRemoveConfirmId(id);
  };

  const confirmRemove = async () => {
    if (!removeConfirmId) return;
    const id = removeConfirmId;
    setRemoveConfirmId(null);
    try {
      await usersApi.remove(id);
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Remove failed');
    }
  };

  const handleSort = (key: string) => {
    const k = key as SortKey;
    if (k === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(k);
      setSortDir(k === 'is_active' ? 'desc' : 'asc');
    }
  };

  // Client-side filter + sort. `GET /api/users` returns the full row set with
  // no query params, so we filter/sort in-memory and let DataTable paginate
  // the result. Fine for a tenant-sized list; if this ever grows past a few
  // hundred users, push `q`/`sort` down to the server.
  const visibleRows = useMemo<AdminUserRow[]>(() => {
    if (!rows) return [];
    const q = searchQuery.trim().toLowerCase();
    const filtered = rows.filter((r) => {
      if (statusFilter === 'active' && !r.is_active) return false;
      if (statusFilter === 'disabled' && r.is_active) return false;
      if (!q) return true;
      return (
        r.display_name.toLowerCase().includes(q) ||
        r.email.toLowerCase().includes(q)
      );
    });
    const sorted = [...filtered].sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'is_active') {
        cmp = (a.is_active ? 1 : 0) - (b.is_active ? 1 : 0);
      } else {
        cmp = String(a[sortKey] ?? '').localeCompare(
          String(b[sortKey] ?? ''),
          undefined,
          { sensitivity: 'base' },
        );
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [rows, searchQuery, statusFilter, sortKey, sortDir]);

  const columns: ColumnDef<AdminUserRow>[] = useMemo(
    () => [
      {
        key: 'display_name',
        label: 'Name',
        sortable: true,
        render: (r) => (
          <span
            className={`text-sm font-medium ${
              r.is_active ? 'text-theme-primary' : 'text-theme-secondary'
            }`}
          >
            {r.display_name || <span className="italic text-theme-secondary">(no name)</span>}
          </span>
        ),
      },
      {
        key: 'email',
        label: 'Email',
        sortable: true,
        render: (r) => (
          <span className={`text-sm ${r.is_active ? 'text-theme-primary' : 'text-theme-secondary'}`}>
            {r.email}
          </span>
        ),
      },
      {
        key: 'role',
        label: 'Role',
        sortable: true,
        headerClassName: 'w-36',
        render: (r) => (
          <select
            value={r.role}
            onChange={(e) => handleRoleChange(r.id, e.target.value as UserRole)}
            onClick={(e) => e.stopPropagation()}
            disabled={!r.is_active}
            className="text-sm border border-line rounded px-2 py-1 bg-surface text-theme-primary focus:ring-1 focus:ring-blue-500 focus:border-transparent disabled:bg-subtle disabled:text-theme-secondary disabled:cursor-not-allowed"
          >
            {ROLES.map((role) => (
              <option key={role} value={role}>
                {role}
              </option>
            ))}
          </select>
        ),
      },
      {
        key: 'aspire_crew_leader_contact_id',
        label: 'Mapped Aspire User',
        headerClassName: 'w-56',
        render: (r) => (
          <div onClick={(e) => e.stopPropagation()} className="min-w-[12rem]">
            <SearchableSelect
              value={r.aspire_crew_leader_contact_id != null ? String(r.aspire_crew_leader_contact_id) : ''}
              onChange={(v) =>
                handleCrewLeaderChange(r.id, v ? Number(v) : null)
              }
              options={(() => {
                const opts = crewLeaders.map((cl) => ({ value: String(cl.contactId), label: cl.name }));
                // Keep an already-saved mapping visible even if that crew leader
                // isn't in the current picker list (e.g. no ticket in the fetched
                // window, or Aspire was briefly unreachable) — otherwise the row
                // would render blank and the save would look like it didn't stick.
                const saved = r.aspire_crew_leader_contact_id;
                if (saved != null && !opts.some((o) => o.value === String(saved))) {
                  opts.unshift({ value: String(saved), label: `Contact ${saved}` });
                }
                return opts;
              })()}
              disabled={!r.is_active}
              placeholder="Unassigned"
              aria-label="Mapped Aspire User"
              className="w-full px-2 py-1 text-sm border border-line rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        ),
      },
      {
        key: 'is_active',
        label: 'Status',
        sortable: true,
        headerClassName: 'w-28',
        render: (r) =>
          r.is_active ? (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-300">
              Active
            </span>
          ) : (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-subtle text-theme-secondary">
              Disabled
            </span>
          ),
      },
      {
        key: 'actions',
        label: '',
        headerClassName: 'w-12',
        render: (r) =>
          r.is_active ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleRemove(r.id);
              }}
              title="Remove user"
              className="p-1.5 text-theme-secondary hover:text-red-500 rounded-lg hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
            >
              <Trash2 size={14} />
            </button>
          ) : null,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [crewLeaders],
  );

  return (
    <div className="max-w-7xl mx-auto h-full flex flex-col overflow-hidden">
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <div>
          <h1 className="text-2xl font-bold text-theme-primary">User Management</h1>
          <p className="text-sm text-theme-secondary mt-1">
            Manage who can sign in and what role they have.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={refresh}
            disabled={loading}
            title="Refresh"
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-line bg-surface hover:bg-subtle transition-colors text-xs font-medium text-theme-secondary disabled:opacity-50"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
          <button
            type="button"
            onClick={() => setShowAdd(true)}
            className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-colors"
          >
            Add User
          </button>
        </div>
      </div>

      <div className="bg-surface rounded-lg shadow-sm border border-line p-3 mb-3 flex-shrink-0">
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-theme-secondary" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name or email…"
              className="w-full pl-8 pr-3 py-1.5 text-sm border border-line rounded-md bg-surface text-theme-primary focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            className="px-2 py-1.5 text-sm border border-line rounded-md bg-surface text-theme-primary focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">All statuses</option>
            <option value="active">Active</option>
            <option value="disabled">Disabled</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 dark:bg-red-500/10 dark:border-red-500/30 dark:text-red-300 px-3 py-2 rounded-md text-sm mb-3 flex-shrink-0">
          {error}
        </div>
      )}

      <DataTable
        data={visibleRows}
        columns={columns}
        keyField="id"
        loading={loading}
        emptyIcon={<Users size={40} className="text-theme-secondary" />}
        emptyTitle={rows && rows.length === 0 ? 'No users yet' : 'No matching users'}
        emptyDescription={
          rows && rows.length === 0
            ? 'Click "Add User" to invite someone from your Entra directory.'
            : 'Try clearing the search or status filter.'
        }
        sortKey={sortKey}
        sortDir={sortDir}
        onSort={handleSort}
        rowClassName={(r) => (r.is_active ? '' : 'opacity-60')}
      />

      {showAdd ? (
        <AddUserDialog
          existingOids={new Set(rows?.map((r) => r.entra_id))}
          onClose={() => setShowAdd(false)}
          onAdded={() => {
            setShowAdd(false);
            refresh();
          }}
        />
      ) : null}

      <ConfirmModal
        isOpen={removeConfirmId !== null}
        title="Remove User"
        message="Remove this user? They will no longer be able to sign in."
        confirmText="Remove"
        cancelText="Cancel"
        variant="danger"
        onConfirm={confirmRemove}
        onCancel={() => setRemoveConfirmId(null)}
      />
    </div>
  );
}

interface AddProps {
  existingOids: Set<string>;
  onClose: () => void;
  onAdded: () => void;
}

function AddUserDialog({ existingOids, onClose, onAdded }: AddProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<PickedUser[]>([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState<PickedUser | null>(null);
  const [role, setRole] = useState<UserRole>('User');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Debounce search.
  useEffect(() => {
    if (!query) {
      setResults([]);
      return;
    }
    const handle = setTimeout(() => {
      setSearching(true);
      usersApi
        .picker(query)
        .then((r) => setResults(r.filter((u) => !existingOids.has(u.oid))))
        .catch((e: Error) => setErr(e.message))
        .finally(() => setSearching(false));
    }, 250);
    return () => clearTimeout(handle);
  }, [query, existingOids]);

  const handleSubmit = async () => {
    if (!selected) return;
    setSubmitting(true);
    setErr(null);
    try {
      await usersApi.create({
        entraId: selected.oid,
        email: selected.mail ?? selected.userPrincipalName,
        displayName: selected.displayName,
        role,
      });
      onAdded();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Add failed');
      setSubmitting(false);
    }
  };

  const visibleResults = useMemo(() => results.slice(0, 25), [results]);

  return (
    <div style={s.modalBackdrop} onClick={onClose}>
      <div style={s.modal} onClick={(e) => e.stopPropagation()}>
        <h2 style={s.modalTitle}>Add User</h2>
        <p style={s.modalNote}>
          Search the Entra directory for an enabled user, then choose a role.
        </p>

        <input
          autoFocus
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setSelected(null);
          }}
          placeholder="Name or email…"
          style={s.input}
        />

        {searching ? <div style={s.muted}>Searching…</div> : null}

        {visibleResults.length > 0 ? (
          <ul style={s.results}>
            {visibleResults.map((u) => {
              const active = selected?.oid === u.oid;
              return (
                <li
                  key={u.oid}
                  style={{ ...s.resultRow, ...(active ? s.resultRowActive : {}) }}
                  onClick={() => setSelected(u)}
                >
                  <strong>{u.displayName}</strong>
                  <span style={s.resultEmail}>{u.mail ?? u.userPrincipalName}</span>
                </li>
              );
            })}
          </ul>
        ) : null}

        {selected ? (
          <div style={s.roleRow}>
            <label style={s.label}>Role</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as UserRole)}
              style={s.select}
            >
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        {err ? <div style={s.error}>{err}</div> : null}

        <div style={s.modalFooter}>
          <button type="button" style={s.secondaryBtn} onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button
            type="button"
            style={s.primaryBtn}
            disabled={!selected || submitting}
            onClick={handleSubmit}
          >
            {submitting ? 'Adding…' : 'Add User'}
          </button>
        </div>
      </div>
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  primaryBtn: {
    padding: '0.5rem 1rem',
    background: '#2563eb',
    color: '#fff',
    border: 'none',
    borderRadius: '0.4rem',
    cursor: 'pointer',
    fontWeight: 500,
  },
  secondaryBtn: {
    padding: '0.5rem 1rem',
    background: 'var(--color-card-bg)',
    color: 'var(--color-text-primary)',
    border: '1px solid var(--color-border)',
    borderRadius: '0.4rem',
    cursor: 'pointer',
  },
  select: { padding: '0.3rem 0.5rem', border: '1px solid var(--color-border)', borderRadius: '0.3rem', background: 'var(--color-card-bg)', color: 'var(--color-text-primary)' },
  muted: { color: 'var(--color-text-secondary)', padding: '1rem 0' },
  error: { background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', padding: '0.6rem 0.8rem', borderRadius: '0.4rem', marginBottom: '1rem' },
  modalBackdrop: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 },
  modal: { background: 'var(--color-card-bg)', borderRadius: '0.6rem', padding: '1.5rem', width: '32rem', maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 20px 25px rgba(0,0,0,0.15)' },
  modalTitle: { fontSize: '1.2rem', fontWeight: 600, margin: '0 0 0.4rem' },
  modalNote: { color: 'var(--color-text-secondary)', fontSize: '0.9rem', margin: '0 0 1rem' },
  input: { width: '100%', padding: '0.6rem', border: '1px solid var(--color-border)', borderRadius: '0.4rem', fontSize: '1rem', marginBottom: '0.75rem', background: 'var(--color-card-bg)', color: 'var(--color-text-primary)' },
  results: { listStyle: 'none', padding: 0, margin: '0 0 1rem', maxHeight: '15rem', overflowY: 'auto', border: '1px solid var(--color-border)', borderRadius: '0.4rem' },
  resultRow: { display: 'flex', flexDirection: 'column', padding: '0.5rem 0.75rem', cursor: 'pointer', borderBottom: '1px solid var(--color-border)' },
  resultRowActive: { background: 'rgb(128 128 128 / 0.12)' },
  resultEmail: { color: 'var(--color-text-secondary)', fontSize: '0.85rem' },
  roleRow: { display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' },
  label: { fontWeight: 500, color: 'var(--color-text-primary)' },
  modalFooter: { display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' },
};
